/**
 * What the bell in the top bar can be ringing about.
 *
 * It is a vocabulary and not a list of two, deliberately. The bell is one place where
 * everything waiting for a PERSON collects — as against the chessboard's "Zahteva
 * obravnavo" strip, which collects what is waiting on a ROOM, and the reminders in the
 * sidebar, which are dated notes somebody wrote for themselves. Adding a kind means
 * adding a value here and a builder in `libs/notifications.ts`; nothing in the bell
 * itself knows what a discount is.
 *
 * The value is the key of the record it points at, so two kinds over the same record —
 * a request waiting and the answer to it — never collide in a list keyed by id.
 */
export enum NotificationKind {
    /**
     * A discount over somebody else's ceiling, waiting for a reader whose own ceiling
     * covers it. Work, not news: it stays in the bell until it is decided.
     */
    BillChangePending = 'bill_change_pending',

    /**
     * The answer to a discount THIS account asked for. News, not work: it is counted
     * until the bell is opened and then it is gone.
     */
    BillChangeDecided = 'bill_change_decided',

    /**
     * A change to a published shift of MINE that I have not yet said I have seen
     * (docs/OSEBJE.md §7). Work, not news: it stays in the bell until the person
     * presses "Potrjujem" on their own rota, which is the only event in this product
     * that may claim somebody was told.
     */
    RotaChangeMine = 'rota_change_mine',

    /**
     * A change on the rota this reader SCHEDULES that the person has not acknowledged.
     * Also work: an uncovered Saturday nobody knows about is the supervisor's problem
     * long before it is the housekeeper's, and the whole reason the rota records a
     * delivery state at all is that "we sent it" and "she was told" are two sentences.
     */
    RotaChangeUndelivered = 'rota_change_undelivered',

    /**
     * A request for leave waiting for a decision (docs/OSEBJE.md §10). Work: it stays
     * until somebody answers it.
     *
     * The reader's OWN request is never in it. Nobody decides their own, so it would be a
     * row with nothing to press — and a bell whose rows cannot all be acted on is a bell
     * people stop opening.
     */
    AbsenceRequestPending = 'absence_request_pending',

    /**
     * The answer to leave THIS account asked for. News, like a decided discount: counted
     * until the bell is opened and gone afterwards.
     *
     * A refusal is in it as loudly as a grant. §10 keeps a refused request visible for
     * the reason §9 does, and somebody who is not told is somebody who has booked a
     * flight.
     */
    AbsenceRequestDecided = 'absence_request_decided',

    /**
     * A yearly leave decision issued to THIS account that has not been acknowledged
     * (docs/OSEBJE-UI.md §5). Work and not news: it stays in the bell until the person
     * presses „Potrjujem“ on their own page — the same shape as an unacknowledged shift
     * change, and for the same reason. What the press records is RECEIPT and not
     * agreement, which the screen says in words and the bell does not have to.
     */
    VacationDecisionIssued = 'vacation_decision_issued',
}
